import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useBusiness } from '../context/BusinessContext';
import {
  listInvoices,
  listPayments,
  listSubscriptions,
  payInvoice,
  Invoice,
  Payment,
  Subscription,
  EmbeddedCheckoutSession,
} from '../services/api';
import EmbeddedCheckoutModal from '../components/EmbeddedCheckoutModal';

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatDate(timestamp: number): string {
  return format(new Date(timestamp * 1000), 'MMM d, yyyy');
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'pending':
      return (
        <span className="inline-flex items-center gap-x-1.5 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
          <ClockIcon className="h-4 w-4" />
          Pending
        </span>
      );
    case 'paid':
    case 'succeeded':
    case 'active':
      return (
        <span className="inline-flex items-center gap-x-1.5 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
          <CheckCircleIcon className="h-4 w-4" />
          {status === 'active' ? 'Active' : 'Paid'}
        </span>
      );
    case 'cancelled':
    case 'canceled':
    case 'failed':
      return (
        <span className="inline-flex items-center gap-x-1.5 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
          <XCircleIcon className="h-4 w-4" />
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
          {status}
        </span>
      );
  }
}

export default function BillingPage() {
  const { currentBusiness } = useBusiness();
  const [activeTab, setActiveTab] = useState<'invoices' | 'payments' | 'subscriptions'>('invoices');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Checkout modal state
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutSession, setCheckoutSession] = useState<EmbeddedCheckoutSession | null>(null);
  const [processingInvoiceId, setProcessingInvoiceId] = useState<string | null>(null);

  useEffect(() => {
    if (currentBusiness) {
      loadBillingData();
    }
  }, [currentBusiness]);

  const loadBillingData = async () => {
    if (!currentBusiness) return;

    setLoading(true);
    setError(null);

    try {
      const [invoicesData, paymentsData, subscriptionsData] = await Promise.all([
        listInvoices(currentBusiness.id),
        listPayments(currentBusiness.id),
        listSubscriptions(currentBusiness.id),
      ]);

      setInvoices(invoicesData);
      setPayments(paymentsData);
      setSubscriptions(subscriptionsData);
    } catch (err: any) {
      console.error('Error loading billing data:', err);
      setError(err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async (invoice: Invoice) => {
    if (!currentBusiness) return;

    setProcessingInvoiceId(invoice.id);
    setError(null);

    try {
      const returnUrl = `${window.location.origin}/billing/return`;
      const session = await payInvoice(currentBusiness.id, invoice.id, returnUrl);
      setCheckoutSession(session);
      setShowCheckout(true);
    } catch (err: any) {
      console.error('Error initiating payment:', err);
      setError(err.message || 'Failed to initiate payment');
    } finally {
      setProcessingInvoiceId(null);
    }
  };

  const tabs = [
    { id: 'invoices' as const, name: 'Invoices', count: invoices.filter(i => i.status === 'pending').length },
    { id: 'payments' as const, name: 'Payment History', count: payments.length },
    { id: 'subscriptions' as const, name: 'Subscriptions', count: subscriptions.filter(s => s.status === 'active').length },
  ];

  if (!currentBusiness) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a business to view billing.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your invoices, payments, and subscriptions.
          </p>
        </div>
        <button
          onClick={loadBillingData}
          disabled={loading}
          className="mt-4 sm:mt-0 inline-flex items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium
                ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }
              `}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className={`
                  ml-2 rounded-full px-2.5 py-0.5 text-xs font-medium
                  ${activeTab === tab.id
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-gray-100 text-gray-900'
                  }
                `}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div>
                {invoices.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">No invoices</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      You don't have any invoices yet.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                            Description
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Amount
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Type
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Status
                          </th>
                          <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {invoices.map((invoice) => (
                          <tr key={invoice.id}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                              {invoice.description}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                              {formatCurrency(invoice.amountCents)}
                              {invoice.interval && (
                                <span className="text-gray-500">/{invoice.interval}</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {invoice.type === 'subscription' ? 'Subscription' : 'One-time'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              {getStatusBadge(invoice.status)}
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              {invoice.status === 'pending' && (
                                <button
                                  onClick={() => handlePayInvoice(invoice)}
                                  disabled={processingInvoiceId === invoice.id}
                                  className="text-primary-600 hover:text-primary-900 disabled:opacity-50"
                                >
                                  {processingInvoiceId === invoice.id ? 'Processing...' : 'Pay Now'}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <div>
                {payments.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">No payments</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      You haven't made any payments yet.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                            Date
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Description
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Amount
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {payments.map((payment) => (
                          <tr key={payment.id}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500 sm:pl-6">
                              {formatDate(payment.created)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                              {payment.description || 'Payment'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                              {formatCurrency(payment.amount)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              {getStatusBadge(payment.status)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Subscriptions Tab */}
            {activeTab === 'subscriptions' && (
              <div>
                {subscriptions.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <ArrowPathIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">No subscriptions</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      You don't have any active subscriptions.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {subscriptions.map((subscription) => (
                      <div
                        key={subscription.id}
                        className="bg-white shadow ring-1 ring-black ring-opacity-5 rounded-lg p-6"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {subscription.items[0]?.productName || 'Subscription'}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              {formatCurrency(subscription.items[0]?.amount || 0)}
                              /{subscription.items[0]?.interval || 'month'}
                            </p>
                          </div>
                          {getStatusBadge(subscription.status)}
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Current period:</span>
                            <p className="font-medium">
                              {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Started:</span>
                            <p className="font-medium">{formatDate(subscription.created)}</p>
                          </div>
                        </div>

                        {subscription.cancelAtPeriodEnd && (
                          <div className="mt-4 rounded-md bg-yellow-50 p-3">
                            <p className="text-sm text-yellow-700">
                              This subscription will cancel at the end of the current billing period.
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Embedded Checkout Modal */}
      {checkoutSession && (
        <EmbeddedCheckoutModal
          isOpen={showCheckout}
          onClose={() => {
            setShowCheckout(false);
            setCheckoutSession(null);
          }}
          clientSecret={checkoutSession.clientSecret}
          connectedAccountId={checkoutSession.connectedAccountId}
          title="Complete Payment"
        />
      )}
    </div>
  );
}
